import React, { useState, useEffect } from 'react'
import { fetchWithCsrf } from '../utils/csrf'
import Modal from './Modal'

interface Book {
  id: number
  title: string
  author: string
  isbn?: string
  description?: string
  cover_url?: string
}

interface BookOption {
  id: number
  book: Book
  display_order: number
}

interface BookProposal {
  id: number
  options: BookOption[]
  user_vote?: {
    id: number
    rankings: { [key: string]: number }
  }
}

interface BookVotingProps {
  proposal: BookProposal
  onClose: () => void
  onSuccess: () => void
}

export default function BookVoting({ proposal, onClose, onSuccess }: BookVotingProps) {
  const [rankedOptions, setRankedOptions] = useState<BookOption[]>([])
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    // Initialize ranked options
    if (proposal.user_vote?.rankings) {
      // Sort options by existing rankings
      const sorted = [...proposal.options].sort((a, b) => {
        const rankA = proposal.user_vote!.rankings[a.id.toString()] || 999
        const rankB = proposal.user_vote!.rankings[b.id.toString()] || 999
        return rankA - rankB
      })
      setRankedOptions(sorted)
    } else {
      // Default to display order
      setRankedOptions([...proposal.options].sort((a, b) => a.display_order - b.display_order))
    }
  }, [proposal])

  const moveOption = (fromIndex: number, toIndex: number) => {
    const newRanked = [...rankedOptions]
    const [movedItem] = newRanked.splice(fromIndex, 1)
    newRanked.splice(toIndex, 0, movedItem)
    setRankedOptions(newRanked)
  }

  const handleSubmit = async () => {
    setSubmitting(true)
    setError('')

    try {
      // Create rankings object: { option_id: rank }
      const rankings: { [key: string]: number } = {}
      rankedOptions.forEach((option, index) => {
        rankings[option.id.toString()] = index + 1
      })

      const response = await fetchWithCsrf(`/api/v1/book_proposals/${proposal.id}/vote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rankings })
      })

      if (response.ok) {
        onSuccess()
        onClose()
      } else {
        const data = await response.json()
        setError(data.error || 'Failed to submit vote')
      }
    } catch (err) {
      setError('Failed to submit vote')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal isOpen={true} onClose={onClose} size="lg" showCloseButton={true}>
      {/* Header */}
      <div className="p-6 border-b border-gray-200">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Rank Your Choices</h2>
          <p className="text-gray-600">Drag to reorder, or use arrows. Your #1 choice gets the most points.</p>
        </div>
      </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 pb-6 pt-4">
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          )}

          <div className="space-y-3">
            {rankedOptions.map((option, index) => (
              <div
                key={option.id}
                className="flex items-center gap-4 p-4 bg-white border-2 border-gray-200 rounded-xl hover:border-indigo-300 transition-all"
              >
                {/* Rank Number */}
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 text-white font-bold flex items-center justify-center text-lg">
                    {index + 1}
                  </div>
                </div>

                {/* Book Info */}
                <div className="flex gap-4 flex-1 min-w-0">
                  {option.book.cover_url ? (
                    <img
                      src={option.book.cover_url}
                      alt={option.book.title}
                      className="w-12 h-18 object-cover rounded shadow-sm flex-shrink-0"
                    />
                  ) : (
                    <div className="w-12 h-18 bg-gradient-to-br from-indigo-100 to-purple-100 rounded flex items-center justify-center flex-shrink-0">
                      <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                      </svg>
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-gray-900">{option.book.title}</h3>
                    <p className="text-sm text-gray-600">by {option.book.author}</p>
                  </div>
                </div>

                {/* Move Buttons */}
                <div className="flex flex-col gap-1">
                  <button
                    onClick={() => moveOption(index, index - 1)}
                    disabled={index === 0}
                    className="p-1 text-gray-400 hover:text-indigo-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    title="Move up"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                    </svg>
                  </button>
                  <button
                    onClick={() => moveOption(index, index + 1)}
                    disabled={index === rankedOptions.length - 1}
                    className="p-1 text-gray-400 hover:text-indigo-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    title="Move down"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Explainer */}
          <div className="mt-6 p-4 bg-indigo-50 rounded-lg border border-indigo-200">
            <div className="flex gap-3">
              <svg className="w-5 h-5 text-indigo-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div className="text-sm text-indigo-900">
                <p className="font-semibold mb-1">How ranked-choice voting works:</p>
                <p className="text-indigo-800">
                  Your #1 choice gets {rankedOptions.length} points, #2 gets {rankedOptions.length - 1} points, and so on.
                  The book with the most total points from all members wins!
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200">
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 px-6 py-3 bg-white text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-all border-2 border-gray-300"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={submitting}
              className="flex-1 bg-gradient-to-r from-indigo-500 to-purple-500 text-white py-3 px-6 rounded-lg font-semibold hover:from-indigo-600 hover:to-purple-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-indigo-500/30"
            >
              {submitting ? 'Submitting...' : 'Submit Vote'}
            </button>
          </div>
        </div>
    </Modal>
  )
}
